import { useAuthentication } from '@/hooks/user/useAuthentication';

import type { SavedFilterOwner } from './savedFiltersStorageKey';

/**
 * The Saved Filters module resolves the Saved Filter owner itself. Page adapters describe only the
 * Saved Filter scope and are never asked for an owner, so no call site can omit one and write into
 * a shared bucket.
 */

/**
 * The owner is the internal GreyScout user id (`gsUid`), read from a Firebase custom claim and so
 * unvalidated at runtime. Anything that is not a positive whole number resolves to no owner, which
 * keys like `gsSavedFilters:undefined:...` would otherwise be built from.
 */
export const resolveSavedFilterOwner = (gsUid: unknown): SavedFilterOwner | undefined => {
  const isCoercibleClaim = (typeof gsUid === 'number' || typeof gsUid === 'string') && gsUid !== '';
  const userId = isCoercibleClaim ? Number(gsUid) : Number.NaN;

  return Number.isInteger(userId) && userId > 0 ? String(userId) : undefined;
};

/**
 * No route renders a Saved Filters view without a signed-in user, but nothing in this layer asserts
 * it — the guarantee comes from the gateway chain. An unresolved owner therefore stays possible
 * here, and the session treats it as disabled rather than falling back to an ownerless key.
 */
export const useSavedFilterOwner = (): SavedFilterOwner | undefined => {
  const { user: authUser } = useAuthentication();

  return resolveSavedFilterOwner(authUser?.gsUid);
};
